import { Input, Textarea } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import type { PromptListRecord, PromptVariableListItem } from '@/domain/prompt.types'
import { formatListItems, parseListItems } from '@/domain/prompt-list-items'

/** Mirrors `promptCodeSchema` — lowercase kebab, starting with a letter. */
const PROMPT_CODE_PATTERN = /^[a-z][a-z0-9-]*$/

/** A head start on the code, not an enforcement: the field stays editable. */
const slugify = (name: string): string =>
	name
		.toLowerCase()
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')

export interface PromptListDialogProps {
	open: boolean
	/** Existing lists, so a duplicate code is caught before the request. */
	existing: PromptListRecord[]
	/** The list being edited; absent creates a new one. */
	list?: PromptListRecord | null
	submitting?: boolean
	errorMessage?: string | null
	onSave: (input: { code: string; name: string; items: PromptVariableListItem[] }) => void
	onCancel: () => void
}

/**
 * Author a shared option set that `list`-typed variables reference.
 *
 * One dialog for create and edit: they differ only in what the fields start as and
 * whether the code is still changeable, and two copies of an item format is how one
 * of them quietly stops round-tripping.
 *
 * Items are entered as text rather than a row editor — a list is usually pasted in
 * from wherever it already exists, and a per-row form turns one paste into fifty
 * clicks. The parsed result is echoed back before saving so the shorthand is never
 * guesswork.
 */
export function PromptListDialog({
	open,
	existing,
	list,
	submitting,
	errorMessage,
	onSave,
	onCancel,
}: PromptListDialogProps) {
	const isEdit = list !== null && list !== undefined
	const [name, setName] = useState('')
	const [code, setCode] = useState('')
	// Until the user takes the code over it tracks the name, which makes the common
	// case a single field.
	const [codeEdited, setCodeEdited] = useState(false)
	const [itemsText, setItemsText] = useState('')

	useEffect(() => {
		if (!open) return
		setName(list?.name ?? '')
		setCode(list?.code ?? '')
		// An existing code is fixed, so it never tracks the name.
		setCodeEdited(isEdit)
		setItemsText(list ? formatListItems(list.items) : '')
	}, [open, list, isEdit])

	const effectiveCode = codeEdited ? code.trim() : slugify(name)
	const items = parseListItems(itemsText)

	// A list's own code is not a collision with itself.
	const taken = existing.some((l) => l.code === effectiveCode && l.id !== list?.id)
	const malformed = effectiveCode !== '' && !PROMPT_CODE_PATTERN.test(effectiveCode)
	const codeProblem = taken
		? `'${effectiveCode}' is already used by another list.`
		: malformed
			? 'Code must be lowercase kebab-case, starting with a letter.'
			: null

	const canSubmit = name.trim() !== '' && effectiveCode !== '' && codeProblem === null && items.length > 0

	return (
		<ConfirmDialog
			open={open}
			title={isEdit ? `Edit “${list.name}”` : 'New prompt list'}
			description="A shared option set. Edit it once and every list-typed variable that references it follows."
			confirmLabel={isEdit ? 'Save list' : 'Create list'}
			busyLabel="Saving…"
			submitting={submitting}
			confirmDisabled={!canSubmit}
			errorMessage={errorMessage}
			onConfirm={() => onSave({ code: effectiveCode, name: name.trim(), items })}
			onCancel={onCancel}
		>
			<div className="space-y-3">
				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Name</span>
					<Input value={name} aria-label="List name" onChange={(e) => setName(e.target.value)} />
				</label>

				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Code</span>
					<Input
						value={effectiveCode}
						aria-label="List code"
						className="font-mono"
						// Immutable once created: a variable references a list BY code, so
						// changing it would silently detach every prompt that points here.
						disabled={isEdit}
						onChange={(e) => {
							setCodeEdited(true)
							setCode(e.target.value)
						}}
					/>
					{codeProblem ? (
						<p role="alert" className="mt-1 text-ds-2xs text-ds-danger">
							{codeProblem}
						</p>
					) : (
						<p className="mt-1 text-ds-2xs text-ds-text-subtle">
							{isEdit ? 'Fixed — prompts reference this list by code.' : 'Immutable once created.'}
						</p>
					)}
				</label>

				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
						Options — one per line, as <code>value: Label | prompt text</code>
					</span>
					<Textarea
						rows={10}
						value={itemsText}
						aria-label="List items"
						placeholder={'animal_alone: Animal Alone | the animal alone, no people in frame\nwith_owner: With Owner'}
						className="font-mono"
						onChange={(e) => setItemsText(e.target.value)}
					/>
					{/*
						The third column is the point of this field: the label is written for
						the person choosing, while what a model needs is often different and
						sometimes actively misleading in label form. Omit it and the value is
						sent as-is, which is what most options want.
					*/}
					<p className="mt-1 text-ds-2xs text-ds-text-subtle">
						{items.length === 0
							? 'A list needs at least one option.'
							: `${items.length} option(s), ${items.filter((i) => i.renderAs !== undefined).length} with custom prompt text.`}
					</p>
				</label>
			</div>
		</ConfirmDialog>
	)
}
